<template>
	<div v-show="displayValidationRunning">Running Validation Check...</div>
	<div v-show="!displayValidationRunning">
		<warningBlock :warningMessages="warningMessage" class="mb-4"></warningBlock>
		<warningBlock :warningMessages="errorMessages" header="Errors" class="mb-4"></warningBlock>

		<div class="flex flex-row gap-4 mb-4">
			<!-- Colonna sinistra: Tabs -->
			<div class="flex-1">
				<div v-show="displayResult && resultSaveButton" class="i2Panel">
					<Tabs value="0">
						<TabList @click="changeTab">
							<Tab value="0">
								<FontAwesomeIcon :icon="['far', 'arrows-spin']" title="End of Life" />
								<span class="tabmenu ml-2">Results by Life Cycle Stage</span>
							</Tab>
							<Tab value="1"
								><FontAwesomeIcon :icon="['far', 'boxes-packing']" title="End of Life" /> <span class="tabmenu ml-2">Results by Step</span></Tab
							>
							<Tab value="2"
								><FontAwesomeIcon :icon="['far', 'flask-round-potion']" title="End of Life" />
								<span class="tabmenu ml-2">Results by Material Composition</span></Tab
							>
							<Tab value="3"
								><FontAwesomeIcon :icon="['far', 'trash-can-list']" title="End of Life" />
								<span class="tabmenu ml-2">Results by Process Waste</span></Tab
							>
						</TabList>
						<TabPanels>
							<TabPanel value="0"
								><resultStepProductProcessAG
									ref="resultStepProductProcessAGControl"
									:assessmentId="assessmentId"
									:tabId="props.tabId"
									:tabName="props.tabName"
									:steps="props.steps"
									:activeEmissionFactors="reportsStore.selectedEmissionFactors"
									:calculationOptions="calculationOptions"
									:selectedUnit="calculatedUnitObject"
									:selectedStep="calculatedStepObject"
									:outputQuantityDescription="outputQuantityDescription" /></TabPanel
							><TabPanel value="1"
								><resultByStepProductProcess
									ref="resultByStepProductProcessControl"
									:assessmentId="assessmentId"
									:tabId="props.tabId"
									:tabName="props.tabName"
									:steps="props.steps"
									:activeEmissionFactors="reportsStore.selectedEmissionFactors"
									:calculationOptions="calculationOptions"
									:selectedUnit="calculatedUnitObject"
									:selectedStep="calculatedStepObject"
									:outputQuantityDescription="outputQuantityDescription"
								></resultByStepProductProcess></TabPanel
							><TabPanel value="2"
								><resultByMaterialCompositionProductProcess
									ref="resultByMaterialComposition"
									:assessmentId="assessmentId"
									:tabId="props.tabId"
									:tabName="props.tabName"
									:steps="props.steps"
									:activeEmissionFactors="reportsStore.selectedEmissionFactors"
									:calculationOptions="calculationOptions"
									:selectedUnit="calculatedUnitObject"
									:selectedStep="calculatedStepObject"
									:outputQuantityDescription="outputQuantityDescription"
								></resultByMaterialCompositionProductProcess></TabPanel
							><TabPanel value="3"
								><resultByProcessWasteProductProcess
									ref="resultByProcessWasteProductProcessControl"
									:assessmentId="assessmentId"
									:tabId="props.tabId"
									:tabName="props.tabName"
									:steps="props.steps"
									:activeEmissionFactors="reportsStore.selectedEmissionFactors"
									:calculationOptions="calculationOptions"
									:selectedUnit="calculatedUnitObject"
									:selectedStep="calculatedStepObject"
									:outputQuantityDescription="outputQuantityDescription"
								></resultByProcessWasteProductProcess
							></TabPanel>
							<TabPanel value="4"
								><AssessmentDetailsAG
									ref="assessmentDetailsControl"
									:assessmentId="assessmentId"
									:tabId="props.tabId"
									:activeEmissionFactors="reportsStore.selectedEmissionFactors"
									:isWeighted="isWeighted" /></TabPanel></TabPanels
					></Tabs>
				</div>
				<div v-show="loadingResults">Results are being calculated...</div>
			</div>

			<!-- Colonna destra: Buttons + SelectableList -->
			<div class="flex flex-col gap-4" style="min-width: 400px; max-width: 450px">
				<!-- Buttons -->
				<div class="flex flex-row items-center gap-2 justify-end flex-wrap">
					<Button v-show="!displayResult && noValidationErrors" class="btn p-button" @click="executeResult">Calculate</Button>
					<Button v-show="displayResult" class="btn p-button" @click="executeResult">Recalculate</Button>
					<Button v-show="displayResult" class="btn p-button" :disabled="!resultSaveButton" @click="exportCalculationLog()">Log</Button>
					<Button v-show="displayResult" class="btn p-button" :disabled="!resultSaveButton" @click="exportToBody()">Export</Button>
				</div>
				<!-- Output Quantity Section -->
				<div class="flex flex-col gap-2">
					<label class="font-medium text-gray-700">Output Quantity</label>
					<div class="flex gap-2">
						<InputNumber v-model="outputQuantity" placeholder="Enter quantity" :min="0" :maxFractionDigits="4" class="flex-1" />
						<Dropdown
							v-model="selectedUnit"
							:options="weightUnits"
							optionLabel="unitName"
							optionValue="id"
							placeholder="Select Unit"
							:filter="weightUnits.length > 10"
							class="flex-1"
						/>
					</div>
				</div>

				<!-- Step Selection -->
				<div class="flex flex-col gap-2">
					<label class="font-medium text-gray-700">Step</label>
					<Dropdown
						v-model="selectedStep"
						:options="stepsWithAllOption"
						:optionLabel="(step: Step) => (step.stepNumber === null ? step.stepName! : `${step.stepNumber} - ${step.stepName}`)"
						optionValue="id"
						placeholder="Select Step"
						:filter="stepsWithAllOption.length > 10"
						class="w-full"
					/>
				</div>
				<!-- Weighted Toggle -->
				<div v-if="weightedOptionEnabled" class="flex items-center gap-2">
					<ToggleSwitch v-model="isWeighted" inputId="weightedToggle" />
					<label for="weightedToggle" class="font-medium">Weighted</label>
				</div>

				<!-- SelectableList -->
				<SelectableList
					v-model="reportsStore.selectedEmissionFactorNames"
					:options="reportsStore.emissionFactors"
					option-label="emissionFactorName"
					option-value="emissionFactorName"
					placeholder="Select Emission Factors"
					:filter="true"
				/>

				<!-- Step Flow Visualization -->
				<div v-if="availableSteps.length > 0" :style="{ height: Math.max(300, availableSteps.length * 70) + 'px' }">
					<VueFlowGraph id="product-process-step-graph" :key="stepGraphKey" :nodes="stepGraph.nodes" :edges="stepGraph.edges" disabled>
						<template #overlay>
							<svg
								v-if="highlightPath"
								style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: -1; overflow: visible"
							>
								<path
									:d="highlightPath"
									fill="var(--color-primary, #3b82f6)"
									fill-opacity="0.08"
									stroke="var(--color-primary, #3b82f6)"
									stroke-width="3"
									stroke-linejoin="round"
								/>
							</svg>
						</template>
					</VueFlowGraph>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, onMounted, nextTick, watch, PropType, computed } from "vue"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import InputNumber from "primevue/inputnumber"
import Dropdown from "primevue/dropdown"
import Button from "primevue/button"
import ToggleSwitch from "primevue/toggleswitch"
import MultiSelect from "primevue/multiselect"
import {
	ApiClient,
	CalculationOptions,
	AllResultsOther,
	ExportRequestOther,
	Project,
	FeatureFlagClient,
	Unit,
	Step,
	UnitOfMeasureClient,
	IntermediateStep,
} from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { EventBus } from "@/eventBus"
import warningBlock from "./warningBlock.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import { useReportsStore } from "@/store/reportsStore"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faFloppyDisk,
	faXmarkLarge,
	faArrowsSpin,
	faBoxesPacking,
	faFlaskRoundPotion,
	faRecycle,
	faTrashCanList,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge, faArrowsSpin, faBoxesPacking, faFlaskRoundPotion, faTrashCanList)

import resultStepProductProcess from "../components/resultStepProductProcess.vue"
import resultStepProductProcessAG from "./resultStepProductProcessAG.vue"
import resultByStepProductProcess from "./resultByStepProductProcessAG.vue"
import resultByMaterialCompositionProductProcess from "./resultByMaterialCompositionProductProcessAG.vue"
import resultByProcessWasteProductProcess from "./resultByProcessWasteProductProcessAG.vue"
import SelectableList from "./SelectableList.vue"
import VueFlowGraph from "./VueFlowGraph.vue"
import { Node, Edge } from "@vue-flow/core"
import { layout, getAncestorNodeIds, computeHighlightPath } from "../composables/elkLayout"
import axios from "axios"
import Card from "primevue/card"
import { formatExportTimestamp } from "@/composables/dateTimeHelper"
import AssessmentDetailsAG from "./assessmentDetailsAG.vue"

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())
const unitOfMeasureClient = new UnitOfMeasureClient(import.meta.env.VITE_API_URL?.toString())
const toast = useToast()

const reportsStore = useReportsStore()

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	steps: {
		type: Array as () => Step[],
		default: () => [],
	},
	intermediates: {
		type: Array as () => IntermediateStep[],
		default: () => [],
	},
})

const noValidationErrors = ref(true)
const displayResult = ref(false)
const displayValidationRunning = ref(false)
const warningMessage = ref([] as string[])
const errorMessages = ref([] as string[])
const generalInformationId = ref()
const resultSaveButton = ref(false)
const isWeighted = ref(false)
const weightedOptionEnabled = ref(false)

// New fields for output quantity and step selection
const ALL_STEPS_VALUE = "__ALL_STEPS__"
const outputQuantity = ref<number | undefined>(1)
const selectedUnit = ref<string | undefined>(undefined)
const selectedStep = ref<string | null>(ALL_STEPS_VALUE)

// Computed property for calculation options
const calculationOptions = computed(() => {
	return new CalculationOptions({
		emissionFactorsToUse: reportsStore.selectedEmissionFactorNames,
		useWeightedEmissionFactors: isWeighted.value,
		outputQuantity: outputQuantity.value,
		selectedUnitId: selectedUnit.value,
		selectedStepId: selectedStep.value === ALL_STEPS_VALUE ? undefined : selectedStep.value || undefined,
	})
})
const weightUnits = ref<Unit[]>([] as Unit[])

// Computed property to filter and sort provided steps from props
const availableSteps = computed(() => {
	return props.steps.filter((s) => s.isActive !== false).sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0))
})

// Computed property to include "All Steps" option
const stepsWithAllOption = computed(() => {
	const allStepsOption: Step = {
		id: ALL_STEPS_VALUE,
		stepNumber: null,
		stepName: "All Steps",
	} as any
	return [allStepsOption, ...availableSteps.value]
})

// Computed properties to get actual Unit and Step objects
const selectedUnitObject = computed(() => {
	return weightUnits.value.find((u) => u.id === selectedUnit.value)
})

const selectedStepObject = computed(() => {
	if (selectedStep.value === ALL_STEPS_VALUE) {
		return new Step({ stepName: "All Steps", stepNumber: undefined } as any)
	}
	return availableSteps.value.find((s) => s.id === selectedStep.value)
})

// Snapshot of step/unit at the time of last calculation
// These are used for display in child component titles so they only update on Calculate click
const calculatedStepObject = ref<Step | undefined>(undefined)
const calculatedUnitObject = ref<Unit | undefined>(undefined)

// Computed property to determine if calculated step is last step or all steps
const isLastStepOrAllSteps = computed(() => {
	if (!calculatedStepObject.value) return true

	// Check if "All Steps" was selected
	if (calculatedStepObject.value.stepNumber === undefined || calculatedStepObject.value.stepNumber === null) {
		return true
	}

	// Find the last step (highest step number)
	const lastStep = availableSteps.value.reduce((max: Step, step) => {
		return (step.stepNumber ?? 0) > (max.stepNumber ?? 0) ? step : max
	}, availableSteps.value[0])

	// Check if calculated step is the last step
	return calculatedStepObject.value.id === lastStep?.id
})

// Computed property for output quantity description
const outputQuantityDescription = computed(() => {
	const quantity = outputQuantity.value ?? 1
	const unitName = calculatedUnitObject.value?.unitName || "kg"

	if (isLastStepOrAllSteps.value) {
		return `per ${quantity} ${unitName} drug product of all steps`
	} else {
		return `per ${quantity} ${unitName} of intermediate drug product`
	}
})

// Step flow visualization
const stepGraph = ref<{ nodes: Node[]; edges: Edge[] }>({ nodes: [], edges: [] })
const stepGraphKey = ref(0)

// Highlight the selected step and all steps that feed into it (ancestors in the flow graph).
const highlightedNodeIds = computed((): Set<string> | undefined => {
	if (!selectedStep.value || selectedStep.value === ALL_STEPS_VALUE) return undefined
	return getAncestorNodeIds(selectedStep.value, stepGraph.value.edges)
})

const highlightPath = computed(() => (highlightedNodeIds.value ? computeHighlightPath(stepGraph.value.nodes, highlightedNodeIds.value) : ""))

//communication with sub tabs
const childResultByLifeStage = ref()
const childResultByStep = ref()
const childResultByMaterialComposition = ref()
const childREsultBYProcessWaste = ref()
const tabData = ref()
const status = ref()
const loadingResults = ref(false)

//child components
const resultByMaterialComposition = ref<InstanceType<typeof resultByMaterialCompositionProductProcess>>()
const resultByStepProductProcessControl = ref<InstanceType<typeof resultByStepProductProcess>>()
// const resultStepProductProcessControl = ref<InstanceType<typeof resultStepProductProcess>>()
const resultStepProductProcessAGControl = ref<InstanceType<typeof resultStepProductProcessAG>>()
const resultByProcessWasteProductProcessControl = ref<InstanceType<typeof resultByProcessWasteProductProcess>>()
const assessmentDetailsControl = ref<InstanceType<typeof AssessmentDetailsAG>>()

// Build step graph for visualization
async function buildStepGraph(steps: Step[]) {
	if (steps.length === 0) {
		return { nodes: [], edges: [] }
	}

	try {
		// Find the final step (highest step number)
		const finalStep = steps.reduce((max: Step, step) => {
			return (step.stepNumber ?? 0) > (max.stepNumber ?? 0) ? step : max
		})

		// Create edges from intermediates
		const edges = props.intermediates.map(
			(i) =>
				({
					id: i.id,
					source: i.intermediateStepId,
					target: i.stepId,
					markerEnd: "arrowclosed",
				}) as Edge
		)

		// Create nodes from steps
		const nodes = steps.map((s) => {
			const isFinalStep = s.id === finalStep?.id
			const isTarget = edges.some((e) => e.target === s.id)

			let type = "step"
			if (isFinalStep) type = "final"
			else if (isTarget) type = "has-intermediate"

			return {
				id: s.id,
				type: type,
				data: {
					label: s.stepNumber,
					status: "healthy",
					errors: [],
					tooltip: `${s.stepNumber}-${s.stepName}`,
				},
			} as Node
		})

		// Use layout function to position nodes
		const result = await layout(nodes, edges, "DOWN")

		return result
	} catch (error) {
		console.error("Error building step graph:", error)
		return { nodes: [], edges: [] }
	}
}

// Load weight-based units from the API
async function loadWeightUnits() {
	try {
		const units = await unitOfMeasureClient.getUnitsByTypeName("Weight")
		weightUnits.value = units || []
		// Sort alphabetically by unit name
		weightUnits.value.sort((a, b) => (a.unitName || "").localeCompare(b.unitName || ""))

		// Set default to kg
		const kgUnit = weightUnits.value.find((u) => u.unitName?.toLowerCase() === "kg")
		if (kgUnit && !selectedUnit.value) {
			selectedUnit.value = kgUnit.id
		}
	} catch (error) {
		console.error("Error loading weight units:", error)
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Failed to load weight units",
			life: 3000,
		})
	}
}

onMounted(async () => {
	// Check if weighted calculation option is enabled
	const weightedFlag = await featureFlagClient.getByName("Weighted Calculation Option")
	weightedOptionEnabled.value = weightedFlag?.isEnabled || false

	// Load weight-based units
	await loadWeightUnits()

	EventBus.on("resetResultStatus_" + props.tabId, () => {
		resultSaveButton.value = false
	})
	EventBus.on("readyToSaveResult_" + props.tabId, () => {
		resultSaveButton.value = true
		loadingResults.value = false
	})
	EventBus.on("PProcesschildResultByLifeStage_" + props.tabId, (data: any) => {
		childResultByLifeStage.value = data
	})
	EventBus.on("PProcesschildResultByStep_" + props.tabId, (data: any) => {
		childResultByStep.value = data
	})
	EventBus.on("PProcesschildResultByMaterialComposition_" + props.tabId, (data: any) => {
		childResultByMaterialComposition.value = data
	})
	EventBus.on("PProcesschildResultByProcessWaste_" + props.tabId, (data: any) => {
		childREsultBYProcessWaste.value = data
	})
	//67441
	EventBus.on("validateResult_" + props.tabId, (data: any) => {
		if (data != null && data.generalInformationId != null) {
			generalInformationId.value = data.generalInformationId
			tabData.value = data
			displayValidationRunning.value = true
			validateResultMatrix(props.tabId, data)
		}
	})
})

function exportCalculationLog() {
	var generalInfomationId = tabData.value.generalInformationId

	apiClient.getProductProcessCalculationLog(generalInfomationId, calculationOptions.value).then(
		(res) => {
			var fileURL = window.URL.createObjectURL(new Blob([res.data]))
			var fileLink = document.createElement("a")

			fileLink.href = fileURL
			var fileName = `Calculation_Log`
			fileLink.setAttribute("download", fileName + ".xlsx")
			document.body.appendChild(fileLink)
			fileLink.click()

			window.URL.revokeObjectURL(fileURL)
			document.body.removeChild(fileLink)
		},
		(error) => {
			status.value = error
		}
	)
}

async function exportToBody() {
	if (reportsStore.selectedEmissionFactorNames.length === 0) {
		toast.add({
			severity: "warn",
			summary: "Warning",
			detail: "Please select at least one emission factor to export",
			life: 5000,
		})
		return
	}

	try {
		var allResults = new AllResultsOther()
		allResults.lifeCycleResults = resultStepProductProcessAGControl.value?.getResultData() ?? []
		allResults.lifeCycleResultsByStep = resultByStepProductProcessControl.value?.getResultData() ?? []
		allResults.materialCompositionResults = resultByMaterialComposition.value?.getResultData() ?? []
		allResults.customProcessWasteEOLs = resultByProcessWasteProductProcessControl.value?.getResultData() ?? []

		const exportRequest = new ExportRequestOther({
			allResults: allResults,
			emissionFactorNames: reportsStore.selectedEmissionFactorNames,
			isWeighted: isWeighted.value,
			assessmentId: props.assessmentId,
			generalInformationId: generalInformationId.value ?? undefined,
		})

		const response = await apiClient.downloadReportOthers(props.tabId, exportRequest)

		// Download file
		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL
		const fileName = response.fileName || `Eco-Design ${props.tabName} Report_${Date.now()}.xlsx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()
		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		toast.add({
			severity: "success",
			summary: "Success",
			detail: "Report exported successfully",
			life: 3000,
		})
	} catch (err: any) {
		console.error("Export error:", err)
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Failed to export Excel file. Please try again.",
			life: 3000,
		})
	}
}
async function changeTab() {
	await nextTick()
	// Chart data is now loaded when user manually selects impact category
}

function validateResultMatrix(tabId: string, data: any) {
	if (data != null && data.generalInformationId != null) {
		apiClient.validateResultMatrixByGeneralInformationId(data.generalInformationId).then(
			(res) => {
				displayValidationRunning.value = false
				// Extract messages using ValidationMessage properties
				var warnings = res.filter((item) => item.messageType === "Warning").map((item) => item.messageText!)
				var errors = res.filter((item) => item.messageType === "Error").map((item) => item.messageText!)
				warningMessage.value = warnings
				errorMessages.value = errors
				noValidationErrors.value = res.length === 0
				// Reset displayResult when validation runs (keeps Calculate button visible until user explicitly clicks it)
				displayResult.value = false
			},
			(err) => {
				displayValidationRunning.value = false
				if (err.status == 400) {
					//toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
					displayResult.value = false
					console.error("Error Id:", err.response.errorId)
					warningMessage.value = [err.response.message]
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				}
			}
		)
	}
}

function executeResult() {
	// Only execute if there are no validation errors
	if (!noValidationErrors.value) return

	// Snapshot current selections for display in child titles
	calculatedStepObject.value = selectedStepObject.value
	calculatedUnitObject.value = selectedUnitObject.value

	loadingResults.value = true
	displayResult.value = true

	// EventBus.emit("invokeResult_" + props.tabId, tabData.value)
	resultByMaterialComposition.value!.getResults(generalInformationId.value)
	resultByStepProductProcessControl.value!.getResults(generalInformationId.value)
	// resultStepProductProcessControl.value!.getResults(generalInformationId.value)
	resultStepProductProcessAGControl.value!.getResults(generalInformationId.value)
	resultByProcessWasteProductProcessControl.value!.getResults(generalInformationId.value)
	// loadingResults will be set to false when readyToSaveResult event is triggered
}

watch(
	isWeighted,
	() => {
		if (displayResult.value) {
			executeResult()
		}
	},
	{ flush: "post" }
)

// Watch for changes in steps, intermediates, or selected step to rebuild the graph
watch(
	[availableSteps, () => props.intermediates, selectedStep],
	async ([newSteps, newIntermediates, newSelectedStep]) => {
		if (newSteps.length > 0) {
			stepGraph.value = await buildStepGraph(newSteps)
		} else {
			stepGraph.value = { nodes: [], edges: [] }
		}
		// Always increment key to force re-render and recenter
		stepGraphKey.value++
	},
	{ deep: true, immediate: true }
)
</script>
